import { ChevronLeft, ChevronRight } from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

interface PaginationProps {
  total: number;
  offset: number;
  limit: number;
  onChange: (next: { offset: number; limit: number }) => void;
  pageSizeOptions?: number[];
}

/** Compact page list with ellipsis gaps: 1 … 4 [5] 6 … 20. */
function pageItems(current: number, totalPages: number): (number | "gap")[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const wanted = new Set([1, totalPages, current, current - 1, current + 1]);
  const visible = [...wanted].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);
  const out: (number | "gap")[] = [];
  let prev = 0;
  for (const p of visible) {
    if (p - prev > 1) out.push("gap");
    out.push(p);
    prev = p;
  }
  return out;
}

const NAV_BTN =
  "grid h-9 w-9 place-items-center rounded-[var(--control-r)] border border-[var(--premium-border-strong)] bg-white text-[var(--text-secondary)] transition hover:bg-[var(--canvas)] disabled:cursor-not-allowed disabled:opacity-40 focus-ring";

export function Pagination({
  total,
  offset,
  limit,
  onChange,
  pageSizeOptions = [25, 50, 100],
}: PaginationProps): JSX.Element | null {
  const { t } = useTranslation("common");
  if (total <= 0) return null;

  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const from = offset + 1;
  const to = Math.min(offset + limit, total);
  const isFirst = offset === 0;
  const isLast = offset + limit >= total;

  const goToPage = (page: number): void =>
    onChange({ offset: Math.max(0, (page - 1) * limit), limit });

  return (
    <div
      className="mt-4 flex flex-wrap items-center justify-between gap-3"
      data-testid="pagination"
    >
      <span data-testid="pagination-showing" className="text-[13px] text-[var(--text-subtle)]">
        {t("pagination.showing", { from, to, total })}
      </span>
      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2 text-[13px] text-[var(--text-subtle)]">
          {t("pagination.rows_per_page")}
          <select
            value={String(limit)}
            onChange={(e) => onChange({ offset: 0, limit: Number(e.target.value) })}
            data-testid="pagination-page-size"
            className="h-9 rounded-[var(--control-r)] border border-[var(--premium-border-strong)] bg-white px-2.5 text-sm text-[var(--text-primary)] outline-none transition focus:border-[var(--brand-500)] focus-ring"
            style={{ boxShadow: "var(--sh-xs)" }}
          >
            {pageSizeOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </label>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => onChange({ offset: Math.max(0, offset - limit), limit })}
            disabled={isFirst}
            data-testid="pagination-prev"
            aria-label={t("pagination.prev")}
            className={NAV_BTN}
            style={{ boxShadow: "var(--sh-xs)" }}
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
          </button>
          {pageItems(currentPage, totalPages).map((item, i) =>
            item === "gap" ? (
              <span
                key={`gap-${i}`}
                aria-hidden
                className="grid h-9 w-9 place-items-center text-[var(--text-subtle)]"
              >
                …
              </span>
            ) : (
              <button
                key={item}
                type="button"
                onClick={() => goToPage(item)}
                aria-label={t("pagination.page_of", { current: item, total: totalPages })}
                aria-current={item === currentPage ? "page" : undefined}
                data-testid={`pagination-page-${item}`}
                className={
                  item === currentPage
                    ? "grid h-9 min-w-9 place-items-center rounded-[var(--control-r)] px-2 text-[13px] font-bold text-white focus-ring"
                    : "grid h-9 min-w-9 place-items-center rounded-[var(--control-r)] border border-[var(--premium-border-strong)] bg-white px-2 text-[13px] font-semibold text-[var(--text-secondary)] transition hover:bg-[var(--canvas)] focus-ring"
                }
                style={
                  item === currentPage
                    ? { background: "var(--brand-500)", boxShadow: "0 1px 2px rgba(15,98,254,.4)" }
                    : { boxShadow: "var(--sh-xs)" }
                }
              >
                {item}
              </button>
            ),
          )}
          <button
            type="button"
            onClick={() => onChange({ offset: offset + limit, limit })}
            disabled={isLast}
            data-testid="pagination-next"
            aria-label={t("pagination.next")}
            className={NAV_BTN}
            style={{ boxShadow: "var(--sh-xs)" }}
          >
            <ChevronRight className="h-4 w-4" aria-hidden />
          </button>
        </div>
        <span data-testid="pagination-page" className="sr-only">
          {t("pagination.page_of", { current: currentPage, total: totalPages })}
        </span>
      </div>
    </div>
  );
}
